function normalizeTruckId(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeDispatchDate(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function buildEveningGanttRailUrl(dispatchDate = "") {
  const searchParams = new URLSearchParams();
  const normalizedDispatchDate = normalizeDispatchDate(dispatchDate);
  if (normalizedDispatchDate) {
    searchParams.set("dispatch_date", normalizedDispatchDate);
  }
  searchParams.set("dispatch_period", "evening");
  return `/api/results/latest?${searchParams.toString()}`;
}

export function mergeEveningSuspendedUpdate(currentPayload, update, dispatchDate) {
  if (!update || update.dispatch_date !== dispatchDate || update.dispatch_period !== "evening"
    || !update.runtime_revision || !Array.isArray(update.suspended_deliveries)) return null;
  // Only suspensions changed. Keep the rail's routes and display metadata intact.
  return {
    ...(currentPayload || { assignments: [] }),
    dispatch_date: dispatchDate,
    dispatch_period: "evening",
    ui_dispatch_period: "evening",
    runtime_revision: update.runtime_revision,
    suspended_deliveries: update.suspended_deliveries,
    planning_summary: { ...currentPayload?.planning_summary, ...update.planning_summary },
  };
}

export function scopeEveningGanttRailPayload(payload, dispatchDate) {
  // /results/latest can omit the period: the scoped request remains authoritative.
  const payloadDate = payload?.dispatch_date || payload?.planning_summary?.dispatch_date;
  const payloadPeriod = payload?.ui_dispatch_period || payload?.dispatch_period;
  if ((payloadDate && payloadDate !== dispatchDate)
    || (payloadPeriod && !["evening", "soir"].includes(payloadPeriod))) {
    throw new Error("Le résultat reçu ne correspond pas au dispatch Soir demandé.");
  }
  return { ...payload, dispatch_date: dispatchDate, ui_dispatch_period: "evening" };
}

export function isUsableEveningGanttRailPayload(payload = null) {
  if (
    !payload
    || typeof payload !== "object"
    || Array.isArray(payload)
    || !Array.isArray(payload.assignments)
  ) {
    return false;
  }

  const errorCode = typeof payload.code === "string" ? payload.code.trim() : "";
  const resource = typeof payload.resource === "string" ? payload.resource.trim() : "";
  const sourceDataState = payload.source_data_state && typeof payload.source_data_state === "object"
    ? payload.source_data_state
    : null;
  const hasTruckInventory = payload.assignments.some((assignment) => (
    normalizeTruckId(assignment?.truck_id || assignment?.truckId) !== ""
  ));
  const isMissingLatestOptimization = (
    errorCode === "resource_not_found"
    && resource === "latest_optimization"
  );

  return (
    errorCode === "latest_optimization_stale"
    || sourceDataState?.stale === true
    || (isMissingLatestOptimization && hasTruckInventory)
  );
}

export function mergeDayAndEveningTimelineRows(dayRows = [], eveningRows = []) {
  const normalizedDayRows = Array.isArray(dayRows) ? dayRows.filter(Boolean) : [];
  const normalizedEveningRows = Array.isArray(eveningRows) ? eveningRows.filter(Boolean) : [];
  const eveningRowByTruckId = new Map();

  normalizedEveningRows.forEach((row) => {
    const truckId = normalizeTruckId(row?.assignmentId || row?.truckId);
    if (truckId && !eveningRowByTruckId.has(truckId)) {
      eveningRowByTruckId.set(truckId, row);
    }
  });

  const mergedRows = normalizedDayRows.map((dayRow) => {
    const truckId = normalizeTruckId(dayRow?.assignmentId || dayRow?.truckId);
    const eveningRow = truckId ? eveningRowByTruckId.get(truckId) || null : null;
    if (truckId) {
      eveningRowByTruckId.delete(truckId);
    }
    return {
      assignment: dayRow?.assignment || eveningRow?.assignment || null,
      assignmentId: truckId,
      dayRow,
      eveningRow,
      hasIncidents: Boolean(dayRow?.hasIncidents || eveningRow?.hasIncidents),
      hasTrips: Boolean(dayRow?.hasTrips || eveningRow?.hasTrips),
      truckId,
      truckLabel: dayRow?.truckLabel || eveningRow?.truckLabel || truckId || "Camion",
    };
  });

  normalizedEveningRows.forEach((eveningRow) => {
    const truckId = normalizeTruckId(eveningRow?.assignmentId || eveningRow?.truckId);
    if (!truckId || !eveningRowByTruckId.has(truckId)) {
      return;
    }
    eveningRowByTruckId.delete(truckId);
    mergedRows.push({
      assignment: eveningRow?.assignment || null,
      assignmentId: truckId,
      dayRow: null,
      eveningRow,
      hasIncidents: Boolean(eveningRow?.hasIncidents),
      hasTrips: Boolean(eveningRow?.hasTrips),
      truckId,
      truckLabel: eveningRow?.truckLabel || truckId || "Camion",
    });
  });

  return mergedRows;
}

export function shouldRefreshEveningGanttRail(eventDetail = null, dispatchDate = "") {
  if (!eventDetail || typeof eventDetail !== "object") {
    return false;
  }

  const normalizedDispatchDate = normalizeDispatchDate(dispatchDate);
  const eventDispatchDate = normalizeDispatchDate(
    eventDetail.dispatchDate || eventDetail.dispatch_date,
  );
  const affectedDispatchDates = (
    Array.isArray(eventDetail.affectedDispatchDates)
      ? eventDetail.affectedDispatchDates
      : Array.isArray(eventDetail.affected_dispatch_dates)
        ? eventDetail.affected_dispatch_dates
        : []
  ).map(normalizeDispatchDate).filter(Boolean);

  if (
    normalizedDispatchDate
    && eventDispatchDate
    && eventDispatchDate !== normalizedDispatchDate
    && !affectedDispatchDates.includes(normalizedDispatchDate)
  ) {
    return false;
  }

  if (
    normalizedDispatchDate
    && affectedDispatchDates.length > 0
    && !affectedDispatchDates.includes(normalizedDispatchDate)
    && eventDispatchDate !== normalizedDispatchDate
  ) {
    return false;
  }

  const actionName = typeof eventDetail.actionName === "string"
    ? eventDetail.actionName.trim()
    : "";
  if (actionName === "evening_standard_transfer") {
    return true;
  }

  const eventDispatchPeriod = typeof (eventDetail.dispatchPeriod || eventDetail.dispatch_period) === "string"
    ? (eventDetail.dispatchPeriod || eventDetail.dispatch_period).trim().toLowerCase()
    : "";
  return eventDispatchPeriod === "evening" || eventDispatchPeriod === "soir";
}
